import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import accessTokenService from "@/services/api/accessTokenService";
import store from "@/store/index";
import { getApperClient } from "@/services/apperClient";

const AUTH_STORAGE_KEY = 'facebook_auth_state';
const AUTH_LEGACY_STORAGE_KEY = 'facebook_auth';
const AUTH_COOKIE_NAME = 'facebook_auth_state_cookie';
const AUTH_TTL_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

const getCookieDomain = () => {
  if (typeof window === 'undefined') return null;
  const { hostname } = window.location;
  const parts = hostname.split('.');

  if (parts.length <= 1) {
    return null;
  }

  if (parts.length === 2) {
    return hostname;
  }

  return parts.slice(-2).join('.');
};

const writeAuthCookie = (data) => {
if (typeof document === 'undefined') return;
  try {
    const payload = encodeURIComponent(JSON.stringify(data));
    const expires = new Date(Date.now() + AUTH_TTL_MS).toUTCString();
    const domain = getCookieDomain();
    const domainPart = (domain && typeof domain === 'string' && domain.includes('.')) ? `;domain=.${domain}` : '';
    document.cookie = `${AUTH_COOKIE_NAME}=${payload};expires=${expires};path=/${domainPart};SameSite=Lax`;
  } catch (error) {
    console.error('AUTH_COOKIE_ERROR: Failed to write auth cookie:', error);
  }
};

const readAuthCookie = () => {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split(';') : [];
  const match = cookies.find(cookie => cookie.trim().startsWith(`${AUTH_COOKIE_NAME}=`));
  if (!match) return null;
  try {
    const value = match.trim().substring(AUTH_COOKIE_NAME.length + 1);
    return decodeURIComponent(value);
  } catch (error) {
    console.error('AUTH_COOKIE_ERROR: Failed to parse auth cookie:', error);
    return null;
  }
};

const clearAuthCookie = () => {
  if (typeof document === 'undefined') return;
const domain = getCookieDomain();
  const domainPart = (domain && typeof domain === 'string' && domain.includes('.')) ? `;domain=.${domain}` : '';
  document.cookie = `${AUTH_COOKIE_NAME}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/${domainPart};SameSite=Lax`;
};

const extractLegacyFacebookSession = () => {
  if (typeof window === 'undefined') return null;

  const storageTargets = [];
  if (window.localStorage) {
    storageTargets.push({ source: 'localStorage', store: window.localStorage });
  }
  if (window.sessionStorage) {
    storageTargets.push({ source: 'sessionStorage', store: window.sessionStorage });
  }

  for (const { source, store } of storageTargets) {
    try {
      for (let i = 0; i < store.length; i += 1) {
        const key = store.key(i);
        if (!key) continue;
        const normalizedKey = key.toLowerCase();
        if (!normalizedKey.startsWith('fbss') && !normalizedKey.startsWith('fbsl') && !normalizedKey.startsWith('fbssl_')) {
          continue;
        }

        const rawValue = store.getItem(key);
        if (!rawValue) continue;

        try {
          const parsed = JSON.parse(rawValue);
          const authResponse = parsed?.authResponse || parsed;
          const accessToken = authResponse?.accessToken || authResponse?.access_token;
          const userId = authResponse?.userID || authResponse?.userId || parsed?.userID;

          if (accessToken && userId) {
            return {
              storageKey: key,
              storageSource: source,
              user: {
                id: userId,
                name: parsed?.user?.name || parsed?.name || 'Facebook User'
              },
              accessToken,
              expiresIn: authResponse?.expiresIn || authResponse?.expires_in || null
            };
          }
        } catch (innerError) {
          console.error('AUTH_LEGACY_PARSE_ERROR: Failed to parse legacy FB session value', {
            key,
            source,
            error: innerError
          });
        }
      }
    } catch (storageError) {
      console.error('AUTH_LEGACY_ERROR: Failed to inspect storage for legacy sessions', {
        source,
        error: storageError
      });
    }
  }

  return null;
};

const FacebookAuthContext = createContext();

export const useFacebookAuth = () => {
  const context = useContext(FacebookAuthContext);
  if (!context) {
    throw new Error('useFacebookAuth must be used within a FacebookAuthProvider');
  }
  return context;
};

export const FacebookAuthProvider = ({ children }) => {
  const apperUser = useSelector((s) => s?.user?.user);
  const apperIsAuthed = useSelector((s) => s?.user?.isAuthenticated);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [workspaceTokenFetched, setWorkspaceTokenFetched] = useState(false);
  const [businessAccounts, setBusinessAccounts] = useState([]);
  const [selectedBusinessAccount, setSelectedBusinessAccount] = useState(null);
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAdAccount, setSelectedAdAccount] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initializationError, setInitializationError] = useState(null);
  const [configAppId, setConfigAppId] = useState(null);

  const saveAuthData = async (authData) => {
    // Validate required authentication data before saving
    if (!authData.user || !authData.user.id || !authData.accessToken) {
      console.error('AUTH_SAVE_ERROR: Missing required authentication data', {
        hasUser: !!authData.user,
        hasUserId: !!authData.user?.id,
        hasAccessToken: !!authData.accessToken,
        userObject: authData.user,
        authData
      });
      return;
    }

    try {
      const { accessTokenService } = await import('@/services/api/accessTokenService');

      console.log('AUTH_SAVE_DEBUG: Saving authentication data to database', {
        userId: authData.user?.id,
        hasAccessToken: !!authData.accessToken,
        tokenExpiry: authData.tokenExpiration ? new Date(authData.tokenExpiration).toISOString() : 'no expiry'
      });

      // Save access token to database with error handling
      const expiresAt = authData.tokenExpiration || (Date.now() + AUTH_TTL_MS);
      const saveResult = await accessTokenService.saveAccessToken(
        'workspace',
        authData.accessToken,
        expiresAt
      );

      // Check if save was successful
      const dbSaveFailed = saveResult && !saveResult.success;
      if (dbSaveFailed) {
        console.error('AUTH_SAVE_ERROR: Database save failed:', {
          error: saveResult.error,
          userId: authData.user?.id,
          timestamp: new Date().toISOString()
        });
      }

      // Always include the token in localStorage so restoreSession can recover it
      // even when the Apper session is not available (e.g. after OAuth redirect)
      const expiresAtForStorage = authData.tokenExpiration || (Date.now() + AUTH_TTL_MS);
      const payload = {
        user: authData.user,
        accessToken: authData.accessToken,
        tokenExpiration: expiresAtForStorage,
        selectedBusinessAccount: authData.selectedBusinessAccount ?? null,
        selectedAdAccount: authData.selectedAdAccount ?? null,
        selectedPage: authData.selectedPage ?? null
      };

      // Also push the token to Apper Secrets via edge function so anonymous
      // visitors (employees on the preview URL) can retrieve it without auth.
      try {
        const saveFnName = import.meta.env.VITE_WORKSPACE_TOKEN_SAVE;
        if (saveFnName) {
          const saveResp = await invokeEdgeFunction(saveFnName, {
            token: authData.accessToken,
            expiresAt: expiresAtForStorage
          });
          if (!saveResp?.success) {
            console.warn('WORKSPACE_SECRET_SAVE_WARN:', saveResp?.message);
          }
        }
      } catch (edgeError) {
        console.warn('WORKSPACE_SECRET_SAVE_WARN:', edgeError?.message);
      }

      try {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
        writeAuthCookie(payload);
        console.log('AUTH_SAVE_DEBUG: Successfully saved state to storage', {
          userId: authData.user?.id,
          hasAccessToken: !!authData.accessToken,
          businessAccount: authData.selectedBusinessAccount?.name || 'none',
          adAccount: authData.selectedAdAccount?.name || 'none',
          page: authData.selectedPage?.name || 'none'
        });
      } catch (storageError) {
        console.error('AUTH_SAVE_ERROR: Failed to save to localStorage/cookie:', storageError);
      }
    } catch (error) {
      console.error('AUTH_SAVE_ERROR: Failed to save authentication data:', {
        error: error.message,
        stack: error.stack,
        userId: authData.user?.id,
        timestamp: new Date().toISOString()
      });
      
      // Fallback to localStorage with full auth data if database fails
      try {
        const payload = {
          user: authData.user,
          accessToken: authData.accessToken,
          selectedBusinessAccount: authData.selectedBusinessAccount ?? null,
          selectedAdAccount: authData.selectedAdAccount ?? null,
          selectedPage: authData.selectedPage ?? null
        };
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(payload));
        writeAuthCookie(payload);
        console.log('AUTH_SAVE_DEBUG: Fallback - saved full auth data to localStorage/cookie');
      } catch (fallbackError) {
        console.error('AUTH_SAVE_ERROR: Fallback storage also failed:', fallbackError);
      }
    }
  };

  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;

    const initialize = async () => {
      setInitializationError(null);

      try {
        // Wait for ApperSDK and Facebook SDK to be available
        if (!window.ApperSDK || !window.FB) {
          timeoutId = setTimeout(() => initialize(), 1000);
          return;
        }

        // SDKs are ready — now show loading while we do async work
        setLoading(true);

        // Fetch Facebook App ID from the configuration endpoint
        let appId = import.meta.env.VITE_FACEBOOK_APP_ID || '1238926602897158';
        try {
          const configResponse = await invokeEdgeFunction(import.meta.env.VITE_FACEBOOK_CONFIG, undefined, 'GET');
          if (configResponse?.success && configResponse.data?.appId) {
            appId = configResponse.data.appId;
          }
        } catch (e) {
          console.warn('AUTH_CONFIG_WARN: Using fallback App ID');
        }
        
        setConfigAppId(appId);

        // Initialize the Facebook SDK
        window.FB.init({
          appId,
          cookie: true,
          xfbml: true,
          version: 'v24.0',
          status: true,
        });

        // Check for existing user session
        await restoreSession();

      } catch (error) {
        console.error('Facebook SDK initialization failed:', error);
        if (isMounted) {
          setInitializationError(error.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, []);

  // Once the Apper user is authenticated (Redux state), the SDK session is ready for DB calls.
  // This is the reliable trigger point to fetch the shared workspace token from the DB —
  // critical for users on fresh devices (e.g. employees) who have no localStorage copy.
  useEffect(() => {
    console.log('[WS_TOKEN_DIAG] effect tick', {
      apperIsAuthed,
      apperUserRaw: apperUser,
      apperUserKeys: apperUser ? Object.keys(apperUser) : null,
      workspaceTokenFetched,
      isAuthenticated,
    });
    if (!apperIsAuthed) return;
    if (workspaceTokenFetched) return;
    if (isAuthenticated) { setWorkspaceTokenFetched(true); return; }

    (async () => {
      try {
        console.log('[WS_TOKEN_DIAG] fetching workspace token from DB...');
        const tokenRecord = await accessTokenService.getAccessToken('workspace');
        console.log('[WS_TOKEN_DIAG] DB response:', {
          hasRecord: !!tokenRecord,
          hasToken: !!tokenRecord?.token,
          tokenLength: tokenRecord?.token?.length,
          userIdentifier: tokenRecord?.userIdentifier,
          expiresAt: tokenRecord?.expiresAt ? new Date(tokenRecord.expiresAt).toISOString() : null,
        });
        const token = tokenRecord?.token;
        if (token) {
          setIsAuthenticated(true);
          setAccessToken(token);
          console.log('[WS_TOKEN_DIAG] set isAuthenticated=true');
        } else {
          console.warn('[WS_TOKEN_DIAG] no token returned — record missing or filtered');
        }
        setWorkspaceTokenFetched(true);
      } catch (e) {
        console.warn('[WS_TOKEN_DIAG] exception:', e?.message, e);
        setWorkspaceTokenFetched(true);
      }
    })();
  }, [apperIsAuthed, apperUser?.id, workspaceTokenFetched, isAuthenticated]);

  const restoreSession = async (retryCount = 0) => {
    // Step 1: Try localStorage for instant same-device restore (no Apper session needed)
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        const token = parsed?.accessToken;
        const expiry = parsed?.tokenExpiration;
        const notExpired = !expiry || Date.now() < expiry;
        if (token && notExpired) {
          setIsAuthenticated(true);
          setAccessToken(token);
          if (parsed.user) setUser(parsed.user);
          if (parsed.selectedBusinessAccount) setSelectedBusinessAccount(parsed.selectedBusinessAccount);
          if (parsed.selectedAdAccount) setSelectedAdAccount(parsed.selectedAdAccount);
          if (parsed.selectedPage) setSelectedPage(parsed.selectedPage);
          return;
        }
        // Expired — clear it
        localStorage.removeItem(AUTH_STORAGE_KEY);
        clearAuthCookie();
      }
    } catch { /* ignore localStorage errors */ }

    // Step 2: Try workspace-token-get edge function (works for anonymous
    // visitors — required for employees on the preview URL)
    try {
      const getFnName = import.meta.env.VITE_WORKSPACE_TOKEN_GET;
      if (getFnName) {
        const resp = await invokeEdgeFunction(getFnName, undefined, 'GET');
        const token = resp?.data?.token;
        const expiresAt = resp?.data?.expiresAt;
        const notExpired = !expiresAt || Date.now() < expiresAt;
        if (resp?.success && token && notExpired) {
          setIsAuthenticated(true);
          setAccessToken(token);
          return;
        }
      }
    } catch (edgeError) {
      console.warn('WORKSPACE_SECRET_GET_WARN:', edgeError?.message);
    }

    // Step 3: DB fallback (works when the caller is an authenticated Apper user)
    try {
      const { accessTokenService } = await import('@/services/api/accessTokenService');
      const tokenRecord = await accessTokenService.getAccessToken('workspace');
      const token = tokenRecord?.token;

      if (!token && retryCount < 3) {
        const delay = retryCount === 0 ? 2000 : retryCount === 1 ? 3000 : 5000;
        setLoading(true);
        setTimeout(() => restoreSession(retryCount + 1), delay);
        return;
      }

      if (token) {
        setIsAuthenticated(true);
        setAccessToken(token);
        return;
      }
    } catch (dbError) {
      if (retryCount < 3) {
        const delay = retryCount === 0 ? 2000 : retryCount === 1 ? 3000 : 5000;
        setLoading(true);
        setTimeout(() => restoreSession(retryCount + 1), delay);
        return;
      }
      console.warn('AUTH_RESTORE_WARN: DB restore failed after retries, falling back to FB SDK:', dbError);
    }

    // Fallback: check the FB JS SDK login status (covers FB.login() flows)
    // Only use this path if we are not already authenticated (e.g. from a concurrent login() call)
    window.FB.getLoginStatus(async (response) => {
      if (response.status === 'connected' && !isAuthenticated) {
        const { authResponse } = response;
        try {
          // Fetch user profile
          const userResponse = await new Promise((resolve, reject) => {
            window.FB.api('/me', { fields: 'id,name,email,picture' }, (res) => {
              if (res && !res.error) {
                resolve(res);
              } else {
                reject(res.error || new Error('Failed to fetch user profile'));
              }
            });
          });

          // Save the new token and update state
          const authData = {
            user: userResponse,
            accessToken: authResponse.accessToken,
          };
          await saveAuthData(authData);

          setIsAuthenticated(true);
          setUser(userResponse);
          setAccessToken(authResponse.accessToken);
        } catch (error) {
          // Do NOT call logout() here — it would wipe state set by a concurrent login() call.
          // If the FB SDK session token is invalid, just leave the user unauthenticated.
          console.error('AUTH_RESTORE_WARN: FB SDK session restore failed, ignoring:', error);
        }
      }
    });
  };

  /**
   * Invoke an Apper edge function.
   * Facebook edge functions are authenticated by the publishable key (not the
   * Apper user session), so we try direct fetch FIRST to avoid calling
   * functions.invoke() on the SDK singleton — which can corrupt the singleton's
   * session state and break subsequent fetchRecords() DB calls for clients.
   * The SDK singleton is only used as a fallback if the direct fetch fails.
   */
  const invokeEdgeFunction = async (functionName, body, method = 'POST') => {
    const projectId = import.meta.env.VITE_APPER_PROJECT_ID;
    const publicKey = import.meta.env.VITE_APPER_PUBLIC_KEY;

    // Attempt 1: direct fetch with publishable key (sufficient for all Facebook edge functions)
    const fnPath = `/fn/${projectId}/${functionName}`;
    for (const url of [`${window.location.origin}${fnPath}`, `https://platform.apper.io${fnPath}`]) {
      try {
        const res = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json', 'x-apper-publishable-key': publicKey },
          credentials: 'include',
          body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        if (res.status === 401) { console.warn(`INVOKE_WARN: 401 from ${url}`); continue; }
        const ct = res.headers.get('content-type') || '';
        if (!ct.includes('application/json')) { console.warn(`INVOKE_WARN: non-JSON response from ${url}`); continue; }
        if (res.ok) {
          const data = await res.json();
          if (data?.success) return data;
          console.warn(`INVOKE_WARN: non-success from ${url}:`, data?.message);
        }
      } catch (e) {
        console.warn(`INVOKE_WARN: fetch to ${url} failed:`, e.message);
      }
    }

    // Attempt 2: SDK singleton fallback (avoids touching singleton unless direct fetch failed)
    const sdkClient = getApperClient();
    if (sdkClient) {
      try {
        const opts = { headers: { 'Content-Type': 'application/json' } };
        if (body !== undefined) opts.body = JSON.stringify(body);
        if (method !== 'POST') opts.method = method;
        const sdkResult = await sdkClient.functions.invoke(functionName, opts);
        if (sdkResult?.success) return sdkResult;
        console.warn(`INVOKE_WARN: SDK non-success for ${functionName}:`, sdkResult?.message);
      } catch (e) {
        console.warn(`INVOKE_WARN: SDK threw for ${functionName}:`, e.message);
      }
    }

    return { success: false, message: `All invocation attempts failed for ${functionName}` };
  };

const login = async (authorizationCode, state) => {
    setLoading(true);
    try {
      const redirectUri = (import.meta.env.VITE_FACEBOOK_REDIRECT_URI || `${window.location.origin}/facebook/callback`).trim();
      const functionName = import.meta.env.VITE_FACEBOOK_AUTH;

      const result = await invokeEdgeFunction(functionName, { code: authorizationCode, redirectUri, state });

      if (!result?.success) {
        return { success: false, sessionExpired: true };
      }

      if (result && result.success && result.data?.user && result.data?.accessToken) {
        const { user, accessToken } = result.data;
        const authData = { user, accessToken };

        await saveAuthData(authData);

        setIsAuthenticated(true);
        setUser(user);
        setAccessToken(accessToken);

        toast.success('Successfully connected to Facebook!');
        return true;
      } else {
        throw new Error(result?.message || 'Authentication failed.');
      }
    } catch (error) {
      console.error('Facebook login failed:', error);
      const isSessionExpired =
        error.message === 'Invalid or expired token' ||
        error.message?.toLowerCase().includes('unauthorized');
      if (isSessionExpired) {
        toast.error('Your app session has expired. Please log in again to connect Facebook.');
      } else {
        toast.error(`Failed to connect Facebook: ${error.message}`);
      }
      return { success: false, sessionExpired: isSessionExpired };
    } finally {
      setLoading(false);
    }
  };

const logout = async () => {
    try {
      if (user && user.id) {
        const { accessTokenService } = await import('@/services/api/accessTokenService');
        await accessTokenService.deleteAccessToken('workspace');
      }
    } catch (error) {
      console.error('Error removing token from database during logout:', error);
    } finally {
      // Clear all state
      setIsAuthenticated(false);
      setUser(null);
      setAccessToken(null);
      setBusinessAccounts([]);
      setSelectedBusinessAccount(null);
      setAdAccounts([]);
      setSelectedAdAccount(null);
      setPages([]);
      setSelectedPage(null);
      
      // Clear all localStorage items and cookies
      localStorage.removeItem(AUTH_LEGACY_STORAGE_KEY);
      localStorage.removeItem(AUTH_STORAGE_KEY);
      clearAuthCookie();
      
      if (window.FB) {
        window.FB.logout();
      }
      toast.success('Successfully logged out of Facebook');
    }
  };

  const loadBusinessAccounts = async () => {
    if (!accessToken) return;

    try {
      setLoading(true);

      const result = await invokeEdgeFunction(import.meta.env.VITE_FACEBOOK_BUSINESS_ACCOUNTS, { accessToken });

      if (result.success) {
        const accounts = Array.isArray(result.data) ? result.data : (result.data?.data || []);
        setBusinessAccounts(accounts);
        
        // Auto-select first business account if none selected
        if (accounts.length > 0 && !selectedBusinessAccount) {
          await selectBusinessAccount(accounts[0]);
        }
      } else {
        console.info(`apper_info: Got an error in this function: ${import.meta.env.VITE_FACEBOOK_BUSINESS_ACCOUNTS}. The response body is: ${JSON.stringify(result)}.`);
        // Facebook token-expired codes: 190 (invalid/expired token), 102 (session expired)
        const fbErrorCode = result.error?.error?.code;
        const isTokenExpired =
          fbErrorCode === 190 || fbErrorCode === 102 ||
          result.error === 'Unauthorized' ||
          result.message?.toLowerCase().includes('invalid or expired') ||
          result.message?.toLowerCase().includes('session has expired');
        if (isTokenExpired) {
          toast.error('Your Facebook token has expired. Please reconnect Facebook.');
          await logout();
        } else {
          // Show the actual Facebook error message when available
          const fbMsg = result.error?.error?.message;
          toast.error(fbMsg || result.message || 'Failed to load Business Managers.');
        }
      }
    } catch (error) {
      console.info(`apper_info: Got this error in this function: ${import.meta.env.VITE_FACEBOOK_BUSINESS_ACCOUNTS}. The error is: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const selectBusinessAccount = async (businessAccount) => {
    try {
      setSelectedBusinessAccount(businessAccount);
      setAdAccounts([]);
      setSelectedAdAccount(null);
// Validate user and access token before updating auth data
if (!user || !user.id || !accessToken) {
        console.error('AUTH_BUSINESS_SELECT_ERROR: Missing required authentication data', {
          hasUser: !!user,
          hasUserId: !!user?.id,
          hasAccessToken: !!accessToken,
          userObject: user
        });
        throw new Error('Authentication failed - missing user ID or access token');
      }

      const authData = {
        user,
        accessToken,
        selectedBusinessAccount: businessAccount,
        selectedAdAccount: null,
        selectedPage: null
      };
      
      console.log('AUTH_BUSINESS_SELECT_DEBUG: Saving business account selection', {
        userId: user.id,
        businessAccountName: businessAccount?.name || 'none'
      });
      
      await saveAuthData(authData);
      
      // Load ad accounts for selected business account
      await loadAdAccounts(businessAccount.id);
    } catch (error) {
      toast.error('Failed to select business account');
    }
  };

  const loadAdAccounts = async (businessAccountId) => {
    if (!accessToken || !businessAccountId) return;
    
    try {
      setLoading(true);
      
      const result = await invokeEdgeFunction(import.meta.env.VITE_FACEBOOK_AD_ACCOUNTS, {
        accessToken,
        businessAccountId: businessAccountId === 'personal' ? null : businessAccountId,
      });

      if (result.success) {
        const accounts = Array.isArray(result.data) ? result.data : (result.data?.data || []);
        setAdAccounts(accounts);
        
        // Auto-select first ad account if none selected
        if (accounts.length > 0 && !selectedAdAccount) {
          await selectAdAccount(accounts[0]);
        }
      } else {
        console.info(`apper_info: Got an error in this function: ${import.meta.env.VITE_FACEBOOK_AD_ACCOUNTS}. The response body is: ${JSON.stringify(result)}.`);
        if (result.error === 'Unauthorized' || result.message === 'Invalid or expired token') {
          toast.error('Your session has expired. Please sign out and sign back in.');
        } else {
          toast.error(result.message || 'Failed to load Ad Accounts.');
        }
      }
    } catch (error) {
      console.info(`apper_info: Got this error in this function: ${import.meta.env.VITE_FACEBOOK_AD_ACCOUNTS}. The error is: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const selectAdAccount = async (adAccount) => {
    setSelectedAdAccount(adAccount);
    setPages([]);
    setSelectedPage(null);

// Validate before saving
    if (!user || !user.id || !accessToken) {
      console.error('AUTH_AD_ACCOUNT_SELECT_ERROR: Missing required authentication data', {
        hasUser: !!user,
        hasUserId: !!user?.id,
        hasAccessToken: !!accessToken
      });
      return;
    }

    const authData = {
      user,
      accessToken,
      selectedBusinessAccount,
      selectedAdAccount: adAccount,
      selectedPage: null
    };
    
    console.log('AUTH_AD_ACCOUNT_SELECT_DEBUG: Saving ad account selection', {
      userId: user.id,
      adAccountName: adAccount?.name || 'none'
    });
    
    await saveAuthData(authData);
  };

  const loadPages = async (adAccountId, businessAccountId) => {
    if (!accessToken) return [];
    if (!window.ApperSDK?.ApperClient) {
      console.error('Pages load aborted: ApperSDK is not available');
      return [];
    }
    
    try {
      setLoading(true);

      const resolvedBusinessId = businessAccountId || selectedBusinessAccount?.id;
      const result = await invokeEdgeFunction(import.meta.env.VITE_FACEBOOK_PAGES, {
        accessToken,
        businessAccountId: resolvedBusinessId === 'personal' ? null : (resolvedBusinessId || null),
        adAccountId: adAccountId || selectedAdAccount?.id || null,
      });

      if (result.success) {
        const pagesList = Array.isArray(result.data) ? result.data : (result.data?.data || []);
        setPages(pagesList);

        if (selectedPage && !pagesList.some(page => page.id === selectedPage.id)) {
          setSelectedPage(null);
// Validate before saving auth data
          if (!user || !user.id || !accessToken) {
            console.error('AUTH_AD_ACCOUNT_ALT_ERROR: Missing required authentication data', {
              hasUser: !!user,
              hasUserId: !!user?.id,
              hasAccessToken: !!accessToken
            });
            return;
          }

          console.log('AUTH_AD_ACCOUNT_ALT_DEBUG: Saving alternative ad account selection', {
            userId: user.id,
            adAccountName: selectedAdAccount?.name || 'none'
          });

          await saveAuthData({
            user,
            accessToken,
            selectedBusinessAccount,
            selectedAdAccount,
            selectedPage: null
          });
        }

        if (!pagesList.length) {
          toast.error('No Facebook pages available for the selected account.');
        }

        return pagesList;
      } else {
        console.info(`apper_info: Got an error in this function: ${import.meta.env.VITE_FACEBOOK_PAGES}. The response body is: ${JSON.stringify(result)}.`);
        setPages([]);
        setSelectedPage(null);
        if (result.error === 'Unauthorized' || result.message === 'Invalid or expired token') {
          toast.error('Your session has expired. Please sign out and sign back in.');
        } else if (result?.message) {
          toast.error(result.message);
        }
      }
    } catch (error) {
      console.info(`apper_info: Got this error in this function: ${import.meta.env.VITE_FACEBOOK_PAGES}. The error is: ${error.message}`);
      setPages([]);
      setSelectedPage(null);
      toast.error('Failed to load Facebook pages. Please try again.');
    } finally {
      setLoading(false);
    }

    return [];
  };

const selectPage = async (page) => {
    setSelectedPage(page);

    // Validate before creating auth data for page selection
    if (!user || !user.id || !accessToken) {
      console.error('AUTH_PAGE_SELECT_ERROR: Missing required authentication data', {
        hasUser: !!user,
        hasUserId: !!user?.id,
        hasAccessToken: !!accessToken
      });
      return;
    }

    const authData = {
      user,
      accessToken,
      selectedBusinessAccount,
      selectedAdAccount,
      selectedPage: page
    };
    
    console.log('AUTH_PAGE_SELECT_DEBUG: Saving page selection', {
      userId: user.id,
      pageName: page?.name || 'none'
    });
    
    await saveAuthData(authData);
  };

useEffect(() => {
    const loadBusinessAccountsEffect = async () => {
      if (!accessToken) return;
      if (businessAccounts.length > 0) return;
      if (!window.ApperSDK?.ApperClient) {
        // ApperSDK not ready yet — retry in 500ms
        setTimeout(loadBusinessAccountsEffect, 500);
        return;
      }
      await loadBusinessAccounts();
    };

    loadBusinessAccountsEffect();
  }, [accessToken, businessAccounts.length]);

  useEffect(() => {
    const loadAdAccountsEffect = async () => {
      if (!accessToken || !selectedBusinessAccount?.id) return;
      if (!window.ApperSDK?.ApperClient) {
        setTimeout(loadAdAccountsEffect, 500);
        return;
      }
      await loadAdAccounts(selectedBusinessAccount.id);
    };

    loadAdAccountsEffect();
  }, [accessToken, selectedBusinessAccount?.id]);

  useEffect(() => {
    const loadPagesEffect = async () => {
      if (!accessToken || !selectedAdAccount?.id) return;
      if (!window.ApperSDK?.ApperClient) {
        setTimeout(loadPagesEffect, 500);
        return;
      }
      await loadPages(selectedAdAccount.id, selectedBusinessAccount?.id);
    };

    loadPagesEffect();
  }, [accessToken, selectedAdAccount?.id, selectedBusinessAccount?.id]);
const value = {
    configAppId,
    setConfigAppId,
    isAuthenticated,
    user,
    accessToken,
    businessAccounts,
    selectedBusinessAccount,
    adAccounts,
    selectedAdAccount,
    pages,
    selectedPage,
    loading,
    initializationError,
    login,
    logout,
    loadBusinessAccounts,
    selectBusinessAccount,
    loadAdAccounts,
    selectAdAccount,
    loadPages,
    selectPage
  };

  return (
    <FacebookAuthContext.Provider value={value}>
      {children}
    </FacebookAuthContext.Provider>
  );
};